import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Box,
  Typography
} from '@mui/material';
import axios from 'axios';
import PropTypes from 'prop-types';
import { API_BASE_URL } from '../../../config/config';
import { jwtDecode } from 'jwt-decode'; // Correct import for jwt-decode

const WaterLevelAlarmSettingsDialog = ({
  open,
  onClose,
  sensorsData,
  deviceCode,
  subId,
  onUpdateSuccess // callback if you want to refresh after saving
}) => {
  // Existing states for alarm settings...
  const [alarmPoints, setAlarmPoints] = useState({
    veryLow: '',
    lowLevel: '',
    highLevel: '',
    veryHighLevel: '',
    positiveFlowHigh: '',
    negativeFlowHigh: '',
    positiveFlowLow: '',
    negativeFlowLow: ''
  });
  const [alarmEnabled, setAlarmEnabled] = useState({
    veryLow: false,
    lowLevel: false,
    highLevel: false,
    veryHighLevel: false,
    positiveFlowHigh: false,
    negativeFlowHigh: false,
    positiveFlowLow: false,
    negativeFlowLow: false
  });
  const [enableSMS, setEnableSMS] = useState(false);
  const [enableWhatsApp, setEnableWhatsApp] = useState(false);
  const [totalSmsBalance, setTotalSmsBalance] = useState('');
  const [remainingSmsBalance, setRemainingSmsBalance] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [loading, setLoading] = useState(false);

  // New state for user role
  const [userRole, setUserRole] = useState('');

  // Decode the token on mount (or when the dialog opens)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        // Assume the token contains a userType field
        setUserRole(decoded.userType || '');
      } catch (error) {
        console.error('Invalid token:', error);
      }
    }
  }, []);

  // Load data from sensorsData when the dialog opens
  useEffect(() => {
    if (open && sensorsData && sensorsData.length > 0) {
      const sensor = sensorsData[0];
      if (sensor && sensor.waterLevelSensorData) {
        const alarmPts = sensor.waterLevelSensorData.alarmPoints || {};
        const alarmEnab = sensor.waterLevelSensorData.alarmSettingsEnabled || {};
        const totalBal = sensor.waterLevelSensorData.totalSmsBalance ?? '';
        const remainBal = sensor.waterLevelSensorData.remainingSmsBalance ?? '';
        
        // NEW: read toggles from waterLevelSensorData
        const smsEnabled = sensor.waterLevelSensorData.enableSMS ?? false;
        const waEnabled = sensor.waterLevelSensorData.enableWhatsApp ?? false;

        setAlarmPoints({
          veryLow: alarmPts.veryLow ?? '',
          lowLevel: alarmPts.lowLevel ?? '',
          highLevel: alarmPts.highLevel ?? '',
          veryHighLevel: alarmPts.veryHighLevel ?? '',
          positiveFlowHigh: alarmPts.positiveFlowHigh ?? '',
          negativeFlowHigh: alarmPts.negativeFlowHigh ?? '',
          positiveFlowLow: alarmPts.positiveFlowLow ?? '',
          negativeFlowLow: alarmPts.negativeFlowLow ?? ''
        });

        setAlarmEnabled({
          veryLow: !!alarmEnab.veryLow,
          lowLevel: !!alarmEnab.lowLevel,
          highLevel: !!alarmEnab.highLevel,
          veryHighLevel: !!alarmEnab.veryHighLevel,
          positiveFlowHigh: !!alarmEnab.positiveFlowHigh,
          negativeFlowHigh: !!alarmEnab.negativeFlowHigh,
          positiveFlowLow: !!alarmEnab.positiveFlowLow,
          negativeFlowLow: !!alarmEnab.negativeFlowLow
        });

        setTotalSmsBalance(totalBal.toString());
        setRemainingSmsBalance(remainBal.toString());

        setEnableSMS(smsEnabled);
        setEnableWhatsApp(waEnabled);
      }
    }
  }, [open, sensorsData]);

  // The rest of your event handlers remain the same...
  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleAlarmPointChange = (e) => {
    const { name, value } = e.target;
    setAlarmPoints((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAlarmEnabledChange = (e) => {
    const { name, checked } = e.target;
    setAlarmEnabled((prev) => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleEnableSMSChange = (e) => {
    setEnableSMS(e.target.checked);
  };

  const handleEnableWhatsAppChange = (e) => {
    setEnableWhatsApp(e.target.checked);
  };

  const handleTotalSmsBalanceChange = (e) => {
    setTotalSmsBalance(e.target.value);
  };

  const handleSubmit = async () => {
    // Validate numeric fields for alarm points and sms balance
    for (const [key, val] of Object.entries(alarmPoints)) {
      if (val === '' || isNaN(Number(val))) {
        setSnackbar({
          open: true,
          message: `Invalid numeric value for ${key}`,
          severity: 'error'
        });
        return;
      }
    }
    if (totalSmsBalance === '' || isNaN(Number(totalSmsBalance))) {
      setSnackbar({
        open: true,
        message: 'Invalid numeric value for total SMS balance.',
        severity: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      // Build the payload for your backend
      const payload = {
        veryLow: Number(alarmPoints.veryLow),
        lowLevel: Number(alarmPoints.lowLevel),
        highLevel: Number(alarmPoints.highLevel),
        veryHighLevel: Number(alarmPoints.veryHighLevel),
        positiveFlowHigh: Number(alarmPoints.positiveFlowHigh),
        negativeFlowHigh: Number(alarmPoints.negativeFlowHigh),
        positiveFlowLow: Number(alarmPoints.positiveFlowLow),
        negativeFlowLow: Number(alarmPoints.negativeFlowLow),

        // Alarm checkbox values
        veryLowEnabled: alarmEnabled.veryLow,
        lowLevelEnabled: alarmEnabled.lowLevel,
        highLevelEnabled: alarmEnabled.highLevel,
        veryHighLevelEnabled: alarmEnabled.veryHighLevel,
        positiveFlowHighEnabled: alarmEnabled.positiveFlowHigh,
        negativeFlowHighEnabled: alarmEnabled.negativeFlowHigh,
        positiveFlowLowEnabled: alarmEnabled.positiveFlowLow,
        negativeFlowLowEnabled: alarmEnabled.negativeFlowLow,

        // SMS / WhatsApp toggles
        enableSMS,
        enableWhatsApp,

        // Only include totalSmsBalance if the user role is Admin or Subadmin.
        totalSmsBalance: Number(totalSmsBalance)
        // If you need to update remainingSmsBalance as well, include it similarly.
      };

      const url = `${API_BASE_URL}api/sensor/sensors/${deviceCode}/${subId}/alarm-settings`;
      const response = await axios.patch(url, payload);

      setSnackbar({
        open: true,
        message: 'Alarm settings updated successfully.',
        severity: 'success'
      });

      if (onUpdateSuccess) {
        onUpdateSuccess(response.data);
      }

      setTimeout(() => {
        onClose();
        setLoading(false);
      }, 1000);
    } catch (error) {
      console.error('Error updating alarm settings:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'An error occurred while updating alarm settings.',
        severity: 'error'
      });
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  // Helper to nicely format labels (e.g. from camelCase)
  const formatLabel = (label) => {
    return label
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
  };

  // Determine if the Total SMS Balance field should be editable.
  // Only "Admin" or "Subadmin" roles can edit; other roles see it as read-only.
  const isEditableSmsBalance = userRole === 'Admin' || userRole === 'Subadmin';

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        {/* Stepper-style header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2, background: 'linear-gradient(90deg, #fff3e0 0%, #fff 100%)', borderRadius: 2, mb: 2 }}>
          <Checkbox checked={enableSMS} sx={{ color: '#1976d2', mx: 1 }} />
          <Typography variant="h6" fontWeight={700} color="#d84315">Alarm Settings</Typography>
          <Checkbox checked={enableWhatsApp} sx={{ color: '#43a047', mx: 1 }} />
        </Box>
        <DialogContent sx={{ background: 'linear-gradient(90deg, #fffde7 0%, #fff3e0 100%)', borderRadius: 2 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {Object.entries(alarmPoints).map(([key, value]) => (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <Box sx={{ p: 2, borderRadius: 3, background: 'linear-gradient(90deg, #fff3e0 0%, #fffde7 100%)', boxShadow: '0 1px 4px #ffe0b2' }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: '#d84315' }}>{formatLabel(key)}</Typography>
                  <TextField
                    name={key}
                    type="number"
                    value={value}
                    onChange={handleAlarmPointChange}
                    fullWidth
                    variant="outlined"
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                  <FormControlLabel
                    sx={{ mt: 1 }}
                    control={
                      <Checkbox
                        name={key}
                        checked={alarmEnabled[key]}
                        onChange={handleAlarmEnabledChange}
                        color="primary"
                      />
                    }
                    label={<Typography sx={{ fontWeight: 500, fontSize: 13 }}>Enable {formatLabel(key)}</Typography>}
                  />
                </Box>
              </Grid>
            ))}

            {/* SMS balances in card */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, borderRadius: 3, background: 'linear-gradient(90deg, #e3f2fd 0%, #fff 100%)', boxShadow: '0 1px 4px #e3f2fd' }}>
                <TextField
                  label="Total SMS Balance"
                  name="totalSmsBalance"
                  type="number"
                  value={totalSmsBalance}
                  onChange={handleTotalSmsBalanceChange}
                  fullWidth
                  variant="outlined"
                  InputProps={{ readOnly: !isEditableSmsBalance }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, borderRadius: 3, background: 'linear-gradient(90deg, #e3f2fd 0%, #fff 100%)', boxShadow: '0 1px 4px #e3f2fd' }}>
                <TextField
                  label="Remaining SMS Balance"
                  name="remainingSmsBalance"
                  value={remainingSmsBalance}
                  fullWidth
                  variant="outlined"
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ pr: 3, pb: 2, justifyContent: 'center' }}>
          <Button onClick={handleClose} sx={{ borderRadius: 20, px: 4 }} color="secondary" disabled={loading} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            sx={{ borderRadius: 20, px: 4, boxShadow: '0 2px 8px #d84315' }}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

WaterLevelAlarmSettingsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  sensorsData: PropTypes.arrayOf(
    PropTypes.shape({
      device_code: PropTypes.string.isRequired,
      sensor_sub_id: PropTypes.string.isRequired,
      waterLevelSensorData: PropTypes.shape({
        alarmPoints: PropTypes.shape({
          veryLow: PropTypes.number,
          lowLevel: PropTypes.number,
          highLevel: PropTypes.number,
          veryHighLevel: PropTypes.number,
          positiveFlowHigh: PropTypes.number,
          negativeFlowHigh: PropTypes.number,
          positiveFlowLow: PropTypes.number,
          negativeFlowLow: PropTypes.number
        }),
        alarmSettingsEnabled: PropTypes.shape({
          veryLow: PropTypes.bool,
          lowLevel: PropTypes.bool,
          highLevel: PropTypes.bool,
          veryHighLevel: PropTypes.bool,
          positiveFlowHigh: PropTypes.bool,
          negativeFlowHigh: PropTypes.bool,
          positiveFlowLow: PropTypes.bool,
          negativeFlowLow: PropTypes.bool
        }),
        enableSMS: PropTypes.bool,
        enableWhatsApp: PropTypes.bool,
        totalSmsBalance: PropTypes.number,
        remainingSmsBalance: PropTypes.number
      })
    })
  ).isRequired,
  deviceCode: PropTypes.string.isRequired,
  subId: PropTypes.string.isRequired,
  onUpdateSuccess: PropTypes.func
};

export default WaterLevelAlarmSettingsDialog;
